import { forwardRef, memo, useCallback, useRef } from 'react';
import { SearchIcon } from '@page/icons';

import { useMergedRefs } from '@page/core/hooks/common';
import { Input } from '../../designSystem/Input';
import { cn } from '../../lib/styles';

interface DialogInputProps extends React.ComponentProps<typeof Input> {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  icon?: React.ReactNode;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
}

const DialogInputBase = (
  {
    value,
    onChange,
    placeholder = 'Search',
    autoFocus = true,
    icon = <SearchIcon className="size-5 min-w-5 text-icon-secondary" fill="currentColor" />,
    className,
    ...rest
  }: DialogInputProps,
  forwardedRef: React.ForwardedRef<HTMLInputElement>,
) => {
  const internalRef = useRef<HTMLInputElement | null>(null);
  const ref = useMergedRefs(forwardedRef, internalRef);

  const handleClick = useCallback(() => {
    internalRef.current?.focus();
  }, [internalRef]);

  return (
    <div
      className={cn('flex cursor-text items-center gap-2 px-4 py-2 h-11 border-b border-border-default', className)}
      onClick={handleClick}
    >
      <Input
        ref={ref}
        variant="unstyled"
        autoFocus={autoFocus}
        containerClassName="w-full"
        className="w-full"
        leftIcon={icon}
        leftIconClassName="left-0"
        inputClassName="py-0 pl-7"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        {...rest}
      />
    </div>
  );
};

export const DialogInput = memo(forwardRef(DialogInputBase));
